import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useState, useMemo } from "react"
import { RoutesLink } from "@/utility/RouteLinks"
import { ThrowError } from "@/api/callApi"
import { ApplicationService } from "@/services/Applications/ApplicationService"
import { showToastNotification } from "@/utility/helper"
import { errorTypes } from "@/utility/validation"
import FormField from "@/components/common/FormField"
import MultiSelectSearch from "@/components/common/MultiSelectSearch"
import Card from "@/components/common/Card"
import { Button } from "@/components/ui/button"
import type { Application } from "@/interface/applications.interface"
import { Plus, X, Users, ShieldCheck, User as UserIcon, Search, Loader2, Sparkles } from "lucide-react"
import FullPageLoader from "@/components/common/FullPageLoader"

export interface IAppUserItem {
    id: string;
    name: string;
    email: string;
    phone?: string;
    avatar?: string;
    department?: string;
    designation?: string;
    type: 0 | 1; // 0 = Staff, 1 = Admin
}

const AddApplication = () => {
    const { register, handleSubmit, setValue, reset, watch, clearErrors, formState: { errors } } = useForm<Application>({
        mode: "onChange",
        defaultValues: {
            name: "",
            clientId: "",
            allowedScopes: ["openid", "profile", "email"],
            redirectUris: [],
            isActive: "true",
            siteId: ""
        }
    })
    const navigate = useNavigate()
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isFatchLoading, setIsFatchLoading] = useState(false)
    const [newUri, setNewUri] = useState("")
    const [uriError, setUriError] = useState("")
    const appName = watch("name")
    const redirectUris = watch("redirectUris") || []

    // ── Application Users State ──────────────────────────────────────────────
    const [userSearch, setUserSearch] = useState("")
    const [userFilterType, setUserFilterType] = useState<"all" | "admin" | "staff">("all")
    const [updatingUserId, setUpdatingUserId] = useState<string | null>(null)
    const [appUsers, setAppUsers] = useState<IAppUserItem[]>([])
    const [isUsersLoading] = useState(false)

    // Helper to generate random alphanumeric string
    const generateAlphanumeric = (length: number) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const getApplicationById = async () => {
        if (!id) return
        setIsFatchLoading(true)
        try {
            const response = await ApplicationService.getApplicationById(id)
            if (response?.status && response?.data) {
                const data = response.data
                reset({
                    name: data.name || "",
                    clientId: data.clientId || "",
                    isActive: String(Boolean(data.isActive)),
                    allowedScopes: Array.isArray(data.allowedScopes) ? data.allowedScopes : ["openid", "profile", "email"],
                    redirectUris: Array.isArray(data.redirectUris) ? data.redirectUris : [],
                })
                if (Array.isArray(data.users)) {
                    setAppUsers(data.users as IAppUserItem[])
                }
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsFatchLoading(false)
        }
    }

    useEffect(() => {
        if (id) {
            getApplicationById()
        }
    }, [id])

    // Auto-generate ClientID and Secret based on Name
    useEffect(() => {
        if (!id && appName && appName.trim().length > 2) {
            const prefix = appName.trim().substring(0, 3).toUpperCase();
            const randomId = `${prefix}_${generateAlphanumeric(8)}`;
            setValue("clientId", randomId);
            setValue("clientSecret", generateAlphanumeric(16));
        }
    }, [appName, id, setValue]);

    useEffect(() => {
        register("allowedScopes", { required: "At least one scope is required" })
        register("redirectUris", { validate: (v) => (v && v?.length > 0) || "At least one Redirect URI is required" })
    }, [register])

    const onSubmit = async (data: Application) => {
        setIsLoading(true)
        try {
            const payload: Application = {
                ...data,
                isActive: String(data.isActive) === "true",
                redirectUris: data.redirectUris || [],
                allowedScopes: scopeOptions.map((s) => s.value),
            }
            if (id) {
                const response = await ApplicationService.updateApplication(id, payload)
                if (response?.status) {
                    showToastNotification("success", response.message)
                    navigate(RoutesLink.superAdminApplication)
                }
            } else {
                const response = await ApplicationService.createApplication(payload)
                if (response?.status) {
                    showToastNotification("success", response.message)
                    navigate(RoutesLink.superAdminApplication)
                }
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleAddUri = () => {
        let trimmed = newUri.trim();
        setUriError("");

        if (!trimmed) {
            setUriError("Please enter a Redirect URI");
            return;
        }

        // Auto add http:// for localhost/127.0.0.1 or https:// for domain if protocol is omitted
        if (!/^https?:\/\//i.test(trimmed)) {
            if (/^(localhost|127\.0\.0\.1)/i.test(trimmed)) {
                trimmed = `http://${trimmed}`;
            } else {
                trimmed = `https://${trimmed}`;
            }
        }

        // Robust URL validation via native URL constructor
        let isValid = false;
        try {
            const parsed = new URL(trimmed);
            isValid = (parsed.protocol === "http:" || parsed.protocol === "https:") && Boolean(parsed.hostname);
        } catch {
            isValid = false;
        }

        if (!isValid) {
            setUriError("Please enter a valid URL (e.g. https://www.google.com or http://localhost:3000)");
            return;
        }

        if (redirectUris.includes(trimmed)) {
            setUriError("This Redirect URI has already been added");
            return;
        }

        setValue("redirectUris", [...redirectUris, trimmed], { shouldValidate: true });
        setNewUri("");
        setUriError("");
        clearErrors("redirectUris");
    };

    const handleRemoveUri = (uri: string) => {
        setValue("redirectUris", redirectUris.filter(u => u !== uri), { shouldValidate: true });
    };

    // ── Handle User Type (Admin vs Staff) Change ──────────────────────────────
    const handleUserTypeChange = async (userId: string, newType: 0 | 1) => {
        setUpdatingUserId(userId);
        const previousUsers = [...appUsers];
        try {
            // Optimistic update in UI
            setAppUsers((prev) =>
                prev.map((u) => (u.id === userId ? { ...u, type: newType } : u))
            );

            const response = await ApplicationService.updateUserType(userId, newType);
            if (response?.status) {
                showToastNotification(
                    "success",
                    response.message || `User role updated to ${newType === 1 ? "Admin" : "Staff"} successfully`
                );
            }
        } catch (error) {
            // Revert state on error
            setAppUsers(previousUsers);
            ThrowError(error);
        } finally {
            setUpdatingUserId(null);
        }
    };

    // ── Filtered Users List ──────────────────────────────────────────────────
    const filteredUsers = useMemo(() => {
        return appUsers.filter((user) => {
            const matchesSearch =
                user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                (user.phone && user.phone.includes(userSearch)) ||
                (user.department && user.department.toLowerCase().includes(userSearch.toLowerCase())) ||
                (user.designation && user.designation.toLowerCase().includes(userSearch.toLowerCase()));

            if (!matchesSearch) return false;

            if (userFilterType === "admin") return user.type === 1;
            if (userFilterType === "staff") return user.type === 0;
            return true;
        });
    }, [appUsers, userSearch, userFilterType]);

    const adminCount = useMemo(() => appUsers.filter((u) => u.type === 1).length, [appUsers]);
    const staffCount = useMemo(() => appUsers.filter((u) => u.type === 0).length, [appUsers]);

    const scopeOptions = [
        { value: "openid", label: "OpenID" },
        { value: "profile", label: "Profile" },
        { value: "email", label: "Email" },
    ]

    if (isFatchLoading) {
        return (
            <Layout>
                <FullPageLoader />
            </Layout >
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? "Edit Application" : "Add Application"}
                    isBackButtonVisible={true}
                    onClick={() => navigate(-1)}
                />

                {/* ── Main Application Form ──────────────────────────────────── */}
                <Card className="overflow-visible border border-slate-200/80 shadow-xs">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="flex items-center gap-2.5 pb-4 border-b border-slate-100">
                            <div className="p-2 rounded-xl bg-primary/10 text-primary">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-slate-800">
                                    Application Configuration
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Define OAuth client credentials, scopes, and callback endpoints
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("name", {
                                    ...errorTypes.required("Application Name"),
                                    ...errorTypes.name,
                                })}
                                label="Application Name"
                                placeholder="e.g. Google"
                                error={errors.name?.message}
                                required
                            />

                            <FormField
                                {...register("clientId")}
                                label="Client ID"
                                placeholder="Auto-generated"
                                disabled
                                error={errors.clientId?.message}
                                required
                            />

                            {!id &&
                                <FormField
                                    {...register("clientSecret", !id ? { ...errorTypes.required("Client Secret") } : {})}
                                    label="Client Secret"
                                    placeholder="Auto-generated (Editable)"
                                    error={errors.clientSecret?.message}
                                    disabled={id ? true : false}
                                    required
                                />
                            }

                            <MultiSelectSearch
                                label="Allowed Scopes"
                                required
                                placeholder="Select Scopes"
                                options={scopeOptions}
                                value={scopeOptions.map((s) => s.value)}
                                onChange={() => {}}
                                disabled={true}
                                error={errors.allowedScopes?.message}
                            />

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Redirect URIs <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newUri}
                                        onChange={(e) => {
                                            setNewUri(e.target.value);
                                            if (uriError) setUriError("");
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddUri();
                                            }
                                        }}
                                        placeholder="http://localhost:3000 or https://example.com"
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                    <Button type="button" onClick={handleAddUri} size="icon" className="shrink-0">
                                        <Plus size={18} />
                                    </Button>
                                </div>
                                {uriError && <p className="text-xs text-red-500 mt-1">{uriError}</p>}
                                {errors.redirectUris && !uriError && (
                                    <p className="text-xs text-destructive mt-1">{errors.redirectUris.message}</p>
                                )}

                                <div className="flex flex-wrap gap-2 mt-3">
                                    {redirectUris.map((uri) => (
                                        <div key={uri} className="flex items-center gap-2 bg-primary/5 text-primary border border-primary/20 px-3 py-1.5 rounded-lg text-sm group">
                                            <span>{uri}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveUri(uri)}
                                                className="text-primary/50 hover:text-destructive transition-colors"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <FormField
                                {...register("isActive", { ...errorTypes.required("Status") })}
                                type="select"
                                label="Status"
                                value={String(watch("isActive") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                error={errors.isActive?.message}
                                required
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-slate-100">
                            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? "Saving..." : (id ? "Update Application" : "Create Application")}
                            </Button>
                        </div>
                    </form>
                </Card>

                {/* ── Application Users & Role Assignment Section (Only in Edit Mode) ── */}
                {id && (
                    <Card className="overflow-hidden border border-slate-200/80 shadow-xs p-0">
                        {/* Section Header */}
                        <div className="p-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/70 via-white to-slate-50/70">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-md shadow-primary/20 shrink-0">
                                        <Users className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                                            Application Users & Access Type
                                            <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold border border-slate-200">
                                                {appUsers.length} Users
                                            </span>
                                        </h3>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            Assign role type (<span className="text-amber-600 font-semibold">Admin</span> or <span className="text-blue-600 font-semibold">Staff</span>) for individual users
                                        </p>
                                    </div>
                                </div>

                                {/* Summary Pills */}
                                <div className="flex items-center gap-2 self-start sm:self-auto">
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
                                        <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                        <span>Admins: {adminCount}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold">
                                        <UserIcon className="w-3.5 h-3.5 text-blue-600" />
                                        <span>Staff: {staffCount}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Search & Filter Toolbar */}
                            <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
                                <div className="relative flex-1 w-full">
                                    <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                        placeholder="Search users by name, email, phone, department..."
                                        className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-xl placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    />
                                    {userSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setUserSearch("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {/* Filter Chips */}
                                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl shrink-0 w-full sm:w-auto justify-center sm:justify-start">
                                    <button
                                        type="button"
                                        onClick={() => setUserFilterType("all")}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${userFilterType === "all"
                                            ? "bg-white text-slate-800 shadow-xs"
                                            : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        All ({appUsers.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUserFilterType("admin")}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${userFilterType === "admin"
                                            ? "bg-white text-amber-700 shadow-xs"
                                            : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        Admin ({adminCount})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUserFilterType("staff")}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${userFilterType === "staff"
                                            ? "bg-white text-blue-700 shadow-xs"
                                            : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        Staff ({staffCount})
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable Users List */}
                        <div className="p-6">
                            {isUsersLoading ? (
                                <div className="py-12 flex flex-col items-center justify-center text-slate-400 gap-2">
                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                    <span className="text-sm">Loading users...</span>
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="py-12 text-center text-slate-400 flex flex-col items-center justify-center">
                                    <div className="p-3 bg-slate-100 rounded-full mb-3">
                                        <Users className="w-6 h-6 text-slate-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-slate-600">No users found</p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {userSearch ? "Try adjusting your search criteria" : "No users assigned to this application yet"}
                                    </p>
                                </div>
                            ) : (
                                <div className="max-h-[460px] overflow-y-auto space-y-2.5 pr-2 scrollbar-thin scrollbar-thumb-slate-200">
                                    {filteredUsers.map((user) => {
                                        const isUpdating = updatingUserId === user.id;
                                        const isAdmin = user.type === 1;
                                        const isStaff = user.type === 0;

                                        return (
                                            <div
                                                key={user.id}
                                                className={`flex flex-col sm:flex-row sm:items-center justify-between p-3.5 sm:p-4 rounded-xl border transition-all gap-3 ${isAdmin
                                                    ? "bg-amber-50/20 border-amber-200/60 hover:border-amber-300"
                                                    : "bg-white border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/40"
                                                    }`}
                                            >
                                                {/* User Details */}
                                                <div className="flex items-center gap-3.5 min-w-0">
                                                    <div
                                                        className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 shadow-inner uppercase ${isAdmin
                                                            ? "bg-amber-100 text-amber-800 ring-2 ring-amber-300/60"
                                                            : "bg-slate-100 text-slate-700"
                                                            }`}
                                                    >
                                                        {user.avatar ? (
                                                            <img
                                                                src={user.avatar}
                                                                alt={user.name}
                                                                className="w-full h-full object-cover rounded-full"
                                                            />
                                                        ) : (
                                                            user.name?.charAt(0) || "U"
                                                        )}
                                                    </div>

                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-semibold text-slate-800 truncate">
                                                                {user.name}
                                                            </p>
                                                            {isAdmin && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-md">
                                                                    Admin
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-0.5">
                                                            <span className="truncate">{user.email}</span>
                                                            {user.phone && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>{user.phone}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {(user.department || user.designation) && (
                                                            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-400">
                                                                {user.department && <span>{user.department}</span>}
                                                                {user.department && user.designation && <span>•</span>}
                                                                {user.designation && <span>{user.designation}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Radio Role Selector */}
                                                <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                                                    {isUpdating && (
                                                        <Loader2 className="w-4 h-4 animate-spin text-primary mr-1" />
                                                    )}

                                                    {/* Staff Radio (type = 0) */}
                                                    <label
                                                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border cursor-pointer select-none transition-all text-xs font-semibold ${isStaff
                                                            ? "bg-blue-50 border-blue-300 text-blue-800 shadow-2xs"
                                                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                                                            }`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`user_role_${user.id}`}
                                                            checked={isStaff}
                                                            disabled={isUpdating}
                                                            onChange={() => handleUserTypeChange(user.id, 0)}
                                                            className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                        <UserIcon className={`w-3.5 h-3.5 ${isStaff ? "text-blue-600" : "text-slate-400"}`} />
                                                        <span>Staff</span>
                                                    </label>

                                                    {/* Admin Radio (type = 1) */}
                                                    <label
                                                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border cursor-pointer select-none transition-all text-xs font-semibold ${isAdmin
                                                            ? "bg-amber-50 border-amber-300 text-amber-900 shadow-2xs"
                                                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                                                            }`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`user_role_${user.id}`}
                                                            checked={isAdmin}
                                                            disabled={isUpdating}
                                                            onChange={() => handleUserTypeChange(user.id, 1)}
                                                            className="w-4 h-4 text-amber-600 border-slate-300 focus:ring-amber-500 cursor-pointer"
                                                        />
                                                        <ShieldCheck className={`w-3.5 h-3.5 ${isAdmin ? "text-amber-600" : "text-slate-400"}`} />
                                                        <span>Admin</span>
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    )
}

export default AddApplication